import { useMemo, useState } from 'react';

import { usePlannerState } from '@hooks/usePlannerState';
import { useLeagueContext } from '@hooks/useLeagueContext';
import {
  PlannerApiError,
  isPlannerSeasonClosed,
  type PlannerGameweek,
  type PlannerStateResponse
} from '@api-service/planner';

export type PickViewMode = 'nextGw' | 'next3Gw' | 'priceChanges';

export type UseMyTeamPickStateOptions = {
  enabled?: boolean;
};

export type UseMyTeamPickStateResult = {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isSeasonClosed: boolean;
  /** HTTP status behind a failed planner request, for the error state. */
  errorStatusCode: number | undefined;
  refetch: () => void;
  hasTeam: boolean;
  planner: PlannerStateResponse | null;
  selectedGameweek: number | null;
  setSelectedGameweek: (gameweek: number) => void;
  selectedGameweekData: PlannerGameweek | null;
  availableGameweeks: number[];
  viewMode: PickViewMode;
  setViewMode: (mode: PickViewMode) => void;
};

const findInitialGameweek = (
  planner: PlannerStateResponse | null,
  currentGameweek: number | null
): number | null => {
  if (!planner?.data.gameweeks.length) return null;
  const matchesCurrent = planner.data.gameweeks.find(
    (gameweek) => gameweek.gameweek === currentGameweek
  );
  return matchesCurrent?.gameweek ?? planner.data.gameweeks[0].gameweek;
};

/**
 * Composes planner state with league context and exposes the data the pick
 * page needs to render: the currently selected gameweek, the list of available
 * gameweeks for navigation, and the active view mode for the segmented
 * control.
 */
export const useMyTeamPickState = (
  options: UseMyTeamPickStateOptions = {}
): UseMyTeamPickStateResult => {
  const { enabled = true } = options;

  const { context } = useLeagueContext();
  const {
    data: planner,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasTeam
  } = usePlannerState({ enabled });

  const currentGameweek = context?.currentGameweek?.number ?? null;
  const initialGameweek = findInitialGameweek(planner, currentGameweek);

  const [selectedGameweek, setSelectedGameweek] = useState<number | null>(
    initialGameweek
  );
  const [viewMode, setViewMode] = useState<PickViewMode>('next3Gw');

  const effectiveSelectedGameweek = selectedGameweek ?? initialGameweek;

  const selectedGameweekData = useMemo(() => {
    if (!planner || effectiveSelectedGameweek == null) return null;
    return (
      planner.data.gameweeks.find(
        (gameweek) => gameweek.gameweek === effectiveSelectedGameweek
      ) ?? null
    );
  }, [planner, effectiveSelectedGameweek]);

  const availableGameweeks = useMemo(
    () => planner?.data.gameweeks.map((gameweek) => gameweek.gameweek) ?? [],
    [planner]
  );

  const isSeasonClosed =
    error instanceof PlannerApiError && isPlannerSeasonClosed(error);

  const errorStatusCode =
    error instanceof PlannerApiError ? error.status : undefined;

  return {
    isLoading,
    isFetching,
    isError,
    isSeasonClosed,
    errorStatusCode,
    refetch,
    hasTeam,
    planner,
    selectedGameweek: effectiveSelectedGameweek,
    setSelectedGameweek,
    selectedGameweekData,
    availableGameweeks,
    viewMode,
    setViewMode
  };
};
